import React, {
  useCallback,
  useMemo,
} from 'react';
import CardLight from 'components/CardLight';
import BodyContent from 'components/BodyContent';
import {
  Accordion, Button, Col, Row,
} from 'react-bootstrap';
import { FiDownload } from 'react-icons/fi';
import { DocRequestTemplateCategoryType } from 'types/DocRequestTemplate';
import _ from 'lodash';
import { DocRequestEntity, DocRequestGrouped } from 'types/DocRequest';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import PdfViewerModal from 'components/Modal/PdfViewerModal';

import useDownloadDocRequest from 'hooks/useDownloadDocRequest';
import DocsTableEdit from '../TableEdit';

type DocsViewProps = {
  data?: DocRequestGrouped,
  loading: boolean
  setItem: ( newData: DocRequestEntity ) => void
};

function DocsView( { data, loading, setItem }:DocsViewProps ) {
  const { t } = useTranslation();
  const contacts = useMemo( () => _.groupBy( data?.[DocRequestTemplateCategoryType.physical], 'contactoID' ), [data] );
  const {
    closeFileModal, currentFileUrl, handleViewDocument, showFileModal, clearCachedFile,
  } = useDownloadDocRequest();

  const showPhysical = !!data?.[DocRequestTemplateCategoryType.physical]?.length;
  const showTitular = !!data?.[DocRequestTemplateCategoryType.titular]?.length;
  const showFiscal = !!data?.[DocRequestTemplateCategoryType.fiscal]?.length;
  const showInvestments = !!data?.[DocRequestTemplateCategoryType.investments]?.length;

  const hasDocRequest = showPhysical || showTitular || showFiscal || showInvestments;

  const marginAfterPhysical = showPhysical;
  const marginAfterTitular = marginAfterPhysical || showTitular;
  const marginAfterFiscal = marginAfterTitular || showFiscal;

  const reloadData = useCallback( ( newData: DocRequestEntity ) => {
    setItem( newData );
    clearCachedFile( newData.id );
  }, [clearCachedFile] );

  return (
    <BodyContent
      classNames={{
        body: classNames( 'mt-3 table-docs', {
          'app-component-loading': loading,
        } ),
      }}
    >
      {!hasDocRequest && (
        <h4 className="text--gray12 text-center">{t( 'admin.docs.empty' )}</h4>
      )}
      {!!hasDocRequest && (
      <Row className="justify-content-end">
        <Col xs="auto">
          <Button variant="link text--sm pt-0 gap-2 d-flex" disabled>
            <FiDownload size={20} />
            <span>Descargar todo</span>
          </Button>
        </Col>
      </Row>
      )}
      {!!hasDocRequest && (
        <div className="table-docs">
          {showPhysical && (
            <CardLight>
              <h4 className="text-ellipsis mb-3">Persona física</h4>
              <Accordion
                defaultActiveKey={Object.keys( contacts )?.[0]}
                key={Object.keys( contacts )?.[0]}
                alwaysOpen
              >
                {Object.keys( contacts )?.map( ( item ) => (
                  <Accordion.Item eventKey={item} key={item}>
                    <Accordion.Header>
                      <Row className="w-100 gx-0">
                        <Col sm>
                          {contacts[item]?.[0].contactoName}
                        </Col>
                      </Row>
                    </Accordion.Header>
                    <Accordion.Body className="pe-0">
                      <DocsTableEdit
                        data={contacts[item]}
                        reloadData={reloadData}
                        handleViewDocument={handleViewDocument}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                ) )}
              </Accordion>
            </CardLight>
          )}

          {showTitular && (
            <CardLight extraClass={marginAfterPhysical ? 'mt-3' : undefined}>
              <h4 className="text-ellipsis mb-3">{t( 'admin.docs.titular' )}</h4>
              <DocsTableEdit
                data={data?.[DocRequestTemplateCategoryType.titular]}
                reloadData={reloadData}
                handleViewDocument={handleViewDocument}
              />
            </CardLight>
          )}

          {showFiscal && (
            <CardLight extraClass={marginAfterTitular ? 'mt-3' : undefined}>
              <h4 className="text-ellipsis mb-3">{t( 'admin.docs.fiscalPerson' )}</h4>
              <DocsTableEdit
                data={data?.[DocRequestTemplateCategoryType.fiscal]}
                reloadData={reloadData}
                handleViewDocument={handleViewDocument}
              />
            </CardLight>
          )}

          {showInvestments && (
            <CardLight extraClass={marginAfterFiscal ? 'mt-3' : undefined}>
              <h4 className="text-ellipsis mb-3">{t( 'admin.docs.investmentsDocs' )}</h4>
              <DocsTableEdit
                data={data?.[DocRequestTemplateCategoryType.investments]}
                reloadData={reloadData}
                handleViewDocument={handleViewDocument}
              />
            </CardLight>
          )}
        </div>
      )}

      {showFileModal && currentFileUrl && (
        <PdfViewerModal url={currentFileUrl} onClose={closeFileModal} />
      )}
    </BodyContent>
  );
}

export default DocsView;
